import { useState } from 'react';
import { Button, Flex, Page, PageHeader, Table, Text, TextField } from '@cmaas/ui';
import type { ConfigAuditLogEntry } from '../../api/types';
import { usePage } from '../../api/usePage';

const SUBJECT_TYPES = [
  'tenant',
  'property',
  'provider_config',
  'wallet_settings',
  'pass_template',
  'webhook_subscription',
  'api_credential',
  'portal_user',
] as const;

// F22's administrative/config-change audit — the "who changed what, when"
// screen F24 exists for (docs/P5-NOTES.md D-P5c-3). Deliberately a separate
// screen from Domain events: different table, different retention, and a
// different row shape (actor + before/after diff, not a guest-data payload).
export function AuditLogPage() {
  const [tenantId, setTenantId] = useState('');
  const [subjectType, setSubjectType] = useState('');
  const [subjectId, setSubjectId] = useState('');

  const page = usePage<ConfigAuditLogEntry>(
    ['config-audit-log', tenantId, subjectType, subjectId],
    '/v1/admin/config-audit-log',
    {
      tenant_id: tenantId || undefined,
      subject_type: subjectType || undefined,
      subject_id: subjectId || undefined,
    },
  );
  const entries = page.data?.pages.flatMap((p) => p.data) ?? [];

  return (
    <Page>
      <PageHeader title="Audit log" />
      <Flex gap="3" wrap="wrap">
        <TextField.Root
          placeholder="tenant_id (uuid)"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
        />
        <select value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
          <option value="">All subject types</option>
          {SUBJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <TextField.Root
          placeholder="subject_id (uuid)"
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
        />
      </Flex>

      {page.isLoading ? (
        <Text as="p">Loading…</Text>
      ) : entries.length === 0 ? (
        <Text as="p">No audit entries match this filter.</Text>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Occurred at</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Tenant</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Subject</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Actor</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {entries.map((e) => (
              <Table.Row key={e.id}>
                <Table.Cell>{new Date(e.occurred_at).toLocaleString()}</Table.Cell>
                <Table.Cell>{e.tenant_id}</Table.Cell>
                <Table.Cell>
                  {e.subject_type}:{e.subject_id}
                </Table.Cell>
                <Table.Cell>{e.action}</Table.Cell>
                <Table.Cell>{e.actor}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      {page.hasNextPage && (
        <Button
          type="button"
          variant="soft"
          onClick={() => void page.fetchNextPage()}
          disabled={page.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </Page>
  );
}
